import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { createAssociate } from "../lib/api";
import { HeartHandshake, CheckCircle2, BadgeCheck } from "lucide-react";

const empty = { name: "", phone: "", email: "", age: "", city: "", experience: "", note: "", eligible: false };

export const Associates = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const age = parseInt(form.age, 10);
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Please add your name and phone.");
    if (!age || age < 45) return toast.error("This opportunity is for those above 45 years of age.");
    if (!form.eligible) return toast.error("Please confirm you are 45+ and currently out of a job.");
    setLoading(true);
    try {
      await createAssociate({
        name: form.name, phone: form.phone, email: form.email, age,
        city: form.city, currently_employed: false, experience: form.experience, note: form.note,
      });
      setDone(true);
      toast.success("Application received — our team will call you soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="associates" data-testid="associates" className="py-24 lg:py-32 bg-brand-ink text-brand-cream relative overflow-hidden">
      <div className="grain opacity-20" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.2em] text-brand-gold uppercase mb-5">Join our team</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight">
            Become an
            <span className="italic text-brand-gold"> ElderCare24 associate</span>.
          </h2>
          <p className="mt-5 text-brand-cream/70 leading-relaxed max-w-md">
            Give your experience and warmth a new purpose. We're looking for mature, caring people to visit and
            support elders across Jaipur.
          </p>
          <div className="mt-8 space-y-3">
            <div className="flex items-center gap-3">
              <BadgeCheck className="text-brand-gold shrink-0" size={20} />
              <p className="text-brand-cream/85">Open to those <b>above 45 years</b> of age</p>
            </div>
            <div className="flex items-center gap-3">
              <BadgeCheck className="text-brand-gold shrink-0" size={20} />
              <p className="text-brand-cream/85">For those <b>currently out of a job</b>, seeking meaningful work</p>
            </div>
            <div className="flex items-center gap-3">
              <BadgeCheck className="text-brand-gold shrink-0" size={20} />
              <p className="text-brand-cream/85">Flexible visits across Jaipur, full training provided</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          {done ? (
            <div className="rounded-3xl bg-white/5 border border-white/10 p-10 text-center" data-testid="associate-success">
              <div className="mx-auto h-16 w-16 rounded-full bg-brand-gold/20 flex items-center justify-center mb-5">
                <CheckCircle2 className="text-brand-gold" size={32} />
              </div>
              <h3 className="font-serif text-3xl mb-2">Thank you, {form.name.split(" ")[0]}!</h3>
              <p className="text-brand-cream/70">Your application is received. Our team will call you on {form.phone} soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="associate-form" className="rounded-3xl bg-white/5 border border-white/10 p-8 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <HeartHandshake className="text-brand-gold" size={22} />
                <h3 className="font-serif text-2xl">Apply as an associate</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-cream/80 text-sm mb-1.5 block">Full name</Label>
                  <Input data-testid="assoc-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="bg-white/10 border-white/20 text-brand-cream placeholder:text-brand-cream/40" />
                </div>
                <div>
                  <Label className="text-brand-cream/80 text-sm mb-1.5 block">Age</Label>
                  <Input data-testid="assoc-age" type="number" min="45" value={form.age} onChange={(e) => set("age", e.target.value)} placeholder="45+" className="bg-white/10 border-white/20 text-brand-cream placeholder:text-brand-cream/40" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-cream/80 text-sm mb-1.5 block">Phone</Label>
                  <Input data-testid="assoc-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 ..." className="bg-white/10 border-white/20 text-brand-cream placeholder:text-brand-cream/40" />
                </div>
                <div>
                  <Label className="text-brand-cream/80 text-sm mb-1.5 block">Area in Jaipur</Label>
                  <Input data-testid="assoc-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Locality" className="bg-white/10 border-white/20 text-brand-cream placeholder:text-brand-cream/40" />
                </div>
              </div>
              <div>
                <Label className="text-brand-cream/80 text-sm mb-1.5 block">Past work / experience (optional)</Label>
                <Textarea data-testid="assoc-experience" value={form.experience} onChange={(e) => set("experience", e.target.value)} rows={2} placeholder="Tell us briefly about yourself" className="bg-white/10 border-white/20 text-brand-cream placeholder:text-brand-cream/40" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer" data-testid="assoc-eligible-label">
                <input type="checkbox" data-testid="assoc-eligible" checked={form.eligible} onChange={(e) => set("eligible", e.target.checked)} className="mt-1 h-4 w-4 accent-brand-gold" />
                <span className="text-sm text-brand-cream/80">I confirm I am <b>above 45 years</b> and <b>currently out of a job</b>.</span>
              </label>
              <Button data-testid="assoc-submit" type="submit" disabled={loading} className="w-full rounded-full bg-brand-gold hover:bg-brand-gold/90 text-brand-ink font-semibold h-12">
                {loading ? "Submitting..." : "Submit application"}
              </Button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
